import { compact, flattenDeep, isEmpty, round, uniq } from "lodash";
import { Base } from "../base";
import { DEFAULT_CURRENCY } from "../currency";
import { DEFAULT_LANGUAGE } from "../language";
import { NFT_CACHE_PERIOD, toNFTId } from "../nft";
import { PaymentMethod } from "../payment";
import { Timezone } from "../timezone";
import { toDuration, toJSON, toSearchText, toSlug, unique } from "../utils";
import { NFTEventTier } from "./tier";
export const OWNERSHIP_PROOF_REQUEST_DELAY = 2000;
export const OWNERSHIP_PROOF_EXPIRATION = 15000;
export const OWNERSHIP_PROOF_EXPIRATION_SECOND = round(OWNERSHIP_PROOF_EXPIRATION / 1000);
export const NFT_VALID_BUFFER = NFT_CACHE_PERIOD + OWNERSHIP_PROOF_EXPIRATION;
export const EVENT_START_BUFFER = 12 * 3600000;
export const EVENT_END_BUFFER = 2 * 3600000;
export const TOKEN_EXPIRATION = OWNERSHIP_PROOF_EXPIRATION * 2;
export class NFTEventInfo {
    constructor(params) {
        this.name = (params === null || params === void 0 ? void 0 : params.name) || "";
        this.descriptions = (params === null || params === void 0 ? void 0 : params.descriptions) || [];
        this.organizer = (params === null || params === void 0 ? void 0 : params.organizer) || { name: "" };
        this.url = (params === null || params === void 0 ? void 0 : params.url) || "";
        if (params === null || params === void 0 ? void 0 : params.date)
            this.date = params.date;
        if (params === null || params === void 0 ? void 0 : params.time)
            this.time = params.time;
        if (params === null || params === void 0 ? void 0 : params.location)
            this.location = params.location;
        if (params === null || params === void 0 ? void 0 : params.locationUrl)
            this.locationUrl = params.locationUrl;
        if (params === null || params === void 0 ? void 0 : params.banner)
            this.banner = params.banner;
        if (params === null || params === void 0 ? void 0 : params.hero)
            this.hero = params.hero;
        if (params === null || params === void 0 ? void 0 : params.gallery)
            this.gallery = params.gallery;
        if (params === null || params === void 0 ? void 0 : params.externalLinks)
            this.externalLinks = params.externalLinks;
        if (params === null || params === void 0 ? void 0 : params.faqs)
            this.faqs = params.faqs;
        if (params === null || params === void 0 ? void 0 : params.customActions)
            this.customActions = params.customActions;
    }
    get json() {
        return toJSON(this);
    }
    get searchText() {
        return toSearchText(flattenDeep([
            this.name,
            this.organizer.name || "",
            this.location || "",
            ...this.descriptions.map((d) => [d.title || "", d.content || ""]),
        ]).join(" "));
    }
}
export class NFTEvent extends Base {
    constructor(params) {
        var _a, _b, _c;
        super(params);
        this.organizerId = params.organizerId;
        if (params.projectId)
            this.projectId = params.projectId;
        this.slug = toSlug(params.slug || this.id);
        this.info = {};
        if (!isEmpty(params.info)) {
            for (const language of Object.keys(params.info)) {
                const lang = language;
                if (params.info[lang])
                    this.info[lang] = new NFTEventInfo(params.info[lang]);
            }
        }
        else {
            this.info[DEFAULT_LANGUAGE] = new NFTEventInfo({
                name: "",
            });
        }
        this.start = Number((_a = params.start) !== null && _a !== void 0 ? _a : 0);
        this.end = Number((_b = params.end) !== null && _b !== void 0 ? _b : 9999999999999);
        this.timezone = params.timezone || Timezone["Asia/Hong_Kong"];
        this.tiers = ((_c = params.tiers) === null || _c === void 0 ? void 0 : _c.map((tier) => new NFTEventTier(tier)).sort((a, b) => a.order - b.order)) || [new NFTEventTier()];
        this.networks = uniq(compact(flattenDeep(this.tiers.map((c) => { var _a; return ((_a = c.nftConstraint) === null || _a === void 0 ? void 0 : _a.collections.map((n) => n.network)) || null; }))));
        this.collections = uniq(compact(flattenDeep(this.tiers.map((tier) => {
            var _a;
            return ((_a = tier.nftConstraint) === null || _a === void 0 ? void 0 : _a.collections.map((n) => toNFTId([n.network, n.collectionId]))) || null;
        }))));
        this.tags = params.tags || [];
        this.hidden = !!params.hidden;
        this.version = params.version || 1;
        if (this.isPaid) {
            this.currency = params.currency || DEFAULT_CURRENCY;
            this.paymentMethods = !isEmpty(params.paymentMethods)
                ? params.paymentMethods
                : [PaymentMethod.STRIPE];
        }
    }
    get json() {
        return toJSON(this);
    }
    get duration() {
        return toDuration(this.start, this.end, this.timezone);
    }
    get isWhitelisted() {
        return this.tiers.some((tier) => tier.whitelistConstraint);
    }
    get isTokenGated() {
        return this.tiers.some((tier) => tier.nftConstraint);
    }
    get isMoonpassGated() {
        return this.tiers.some((tier) => tier.moonpassConstraint);
    }
    get isPublic() {
        return !(this.isWhitelisted || this.isTokenGated || this.isMoonpassGated);
    }
    get isPaid() {
        return this.tiers.some((tier) => tier.paymentConfig);
    }
    get hasUsageLimit() {
        return this.tiers.some((tier) => tier.usageConstraint &&
            (tier.usageConstraint.limit > 0 || tier.usageConstraint.boundToUser));
    }
    get hasAirdrop() {
        return this.tiers.some((tier) => tier.airdropConfig &&
            tier.airdropConfig.contractId &&
            tier.airdropConfig.metadataTemplateId);
    }
    get hasAttendanceAirdrop() {
        return this.tiers.some((tier) => tier.attendanceAirdropConfig &&
            tier.attendanceAirdropConfig.contractId &&
            tier.attendanceAirdropConfig.metadataTemplateId);
    }
    get isStarted() {
        return new Date().valueOf() >= this.start;
    }
    get isEnded() {
        return new Date().valueOf() > this.end;
    }
    get searchText() {
        return toSearchText(unique([
            ...(this.tags || []),
            ...Object.keys(this.info).map((language) => {
                var _a;
                const lang = language;
                return ((_a = this.info[lang]) === null || _a === void 0 ? void 0 : _a.searchText) || "";
            }),
        ]).join(" "));
    }
    validate() {
        const now = new Date().valueOf();
        if (now < this.start)
            return false;
        if (now > this.end)
            return false;
        return true;
    }
    updateCollections() {
        this.collections = uniq(compact(flattenDeep(this.tiers.map((tier) => {
            var _a;
            return ((_a = tier.nftConstraint) === null || _a === void 0 ? void 0 : _a.collections.map((n) => toNFTId([n.network, n.collectionId]))) || null;
        }))));
    }
    updateNetworks() {
        this.networks = uniq(compact(flattenDeep(this.tiers.map((c) => { var _a; return ((_a = c.nftConstraint) === null || _a === void 0 ? void 0 : _a.collections.map((n) => n.network)) || null; }))));
    }
    update() {
        this.updateCollections();
        this.updateNetworks();
        this.updatedAt = new Date().valueOf();
    }
    addTier(constraint) {
        this.tiers.push(constraint);
        this.update();
    }
    removeTier(constraintId) {
        this.tiers = this.tiers.filter((c) => c.id != constraintId);
        this.update();
    }
    localeInfo(language) {
        if (language in this.info)
            return this.info[language];
        return this.info[DEFAULT_LANGUAGE] || new NFTEventInfo();
    }
}
